/**
 * Copyright 2025-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useState } from "react";
import { RowSelectionState } from "@tanstack/table-core";
import { Else, If, Then } from "react-if";
import { v4 } from "uuid";
import {
  FutureActionsForResourceSortFields,
  SortOrder,
} from "~/generated/graphql";
import { LoaderWrap } from "~/components/Loader";
import { NoData } from "~/components/NoData";
import { Pagination } from "~/components/Pagination";
import { getNoSearchResultsMessage } from "~/components/SearchInput";
import { TableWrapper } from "~/components/Tables";
import { useQueryState, useGetSearchPhrase } from "~/hooks";
import { isEmpty, isNotNil } from "~/tools";
import { useGetIncomingActionsForResource } from "../../hooks/useGetIncomingActionsForResource/useGetIncomingActionsForResource";
import {
  ResourceDetailsActionsQuery,
  FutureActionForResourceWithId,
} from "../../types/ResourceDetailsActionsQuery";
import { prepareFutureActionsFilter } from "./resourceDetailsActionFilterBuilders";
import { columns } from "./ResourceDetailsIncomingActionsTableColumns";

type Props = {
  resourceId: string;
  onCountChange?: (count: number) => void;
};

export function ResourceDetailsIncomingActionsTable({
  resourceId,
  onCountChange,
}: Props) {
  const { queryState } = useQueryState<ResourceDetailsActionsQuery>();
  const { phrase, isSearchAllowed } = useGetSearchPhrase(
    queryState.searchPhrase
  );
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const {
    data: actions,
    loading,
    count,
  } = useGetIncomingActionsForResource({
    resourceId: resourceId ?? "",
    page: queryState.page ?? "1",
    limit: queryState.limit ?? "20",
    sort: {
      field:
        (queryState.field as FutureActionsForResourceSortFields) ??
        FutureActionsForResourceSortFields.StartTime,
      order: queryState.order ?? SortOrder.Asc,
    },
    filter: prepareFutureActionsFilter(queryState, phrase),
  });
  useEffect(() => {
    onCountChange?.(count);
  }, [count, onCountChange]);

  const actionsWithId = actions.map((action) => ({
    ...action,
    id: v4(),
  }));

  return (
    <>
      <If condition={!isSearchAllowed}>
        <Then>
          <NoData hasIcon message={getNoSearchResultsMessage()} />
        </Then>
        <Else>
          <LoaderWrap loading={loading} inContent>
            <If condition={isEmpty(actionsWithId)}>
              <Then>
                <NoData
                  hasIcon
                  message={"There are no Future Actions with this Resource."}
                />
              </Then>
              <Else>
                <>
                  <TableWrapper<Partial<FutureActionForResourceWithId>>
                    loading={loading}
                    data={actionsWithId.filter(isNotNil)}
                    columns={columns()}
                    rowSelection={rowSelection}
                    setRowSelection={setRowSelection}
                    stripped
                  />
                  <Pagination count={count} />
                </>
              </Else>
            </If>
          </LoaderWrap>
        </Else>
      </If>
    </>
  );
}
